import React, { useEffect, useRef, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Save, Upload } from "lucide-react";
import { uploadToCloudinary } from "@/lib/upload";

const PAGES = [
  { key: "homepage", label: "Homepage" },
  { key: "elearning", label: "E-Learning" },
  { key: "feedback", label: "Kritik & Saran" },
  { key: "contact", label: "Kontak" },
];

// Fields with helpful labels per page
const FIELD_LABELS = {
  homepage: {
    hero_overline: "Hero — Overline",
    hero_since: "Hero — Sejak (kanan)",
    hero_title_a: "Hero — Judul bagian 1",
    hero_title_em1: "Hero — Judul emphasis 1",
    hero_title_b: "Hero — Judul bagian 2",
    hero_title_em2: "Hero — Judul emphasis 2",
    hero_desc: "Hero — Deskripsi",
    hero_cta_primary: "Hero — Tombol utama",
    hero_cta_secondary: "Hero — Tombol sekunder",
    hero_image_url: "Hero — URL gambar utama",
    stat_partners: "Stat 1 — Mitra Aktif",
    stat_pcs: "Stat 2 — Pcs/Bulan",
    stat_provinces: "Stat 3 — Provinsi",
    catalog_overline: "Koleksi Aurora — Section Title (overline)",
    catalog_title: "Koleksi Aurora — Headline utama",
    catalog_subtitle: "Koleksi Aurora — Subtitle deskripsi",
    why_overline: "Mengapa — Overline",
    why_title: "Mengapa — Judul",
    why_desc: "Mengapa — Deskripsi paragraf",
    why_card_1_icon: "Kartu 1 — Ikon",
    why_card_1_title: "Kartu 1 — Judul",
    why_card_1_desc: "Kartu 1 — Deskripsi",
    why_card_2_icon: "Kartu 2 — Ikon",
    why_card_2_title: "Kartu 2 — Judul",
    why_card_2_desc: "Kartu 2 — Deskripsi",
    why_card_3_icon: "Kartu 3 — Ikon",
    why_card_3_title: "Kartu 3 — Judul",
    why_card_3_desc: "Kartu 3 — Deskripsi",
    why_card_4_icon: "Kartu 4 — Ikon",
    why_card_4_title: "Kartu 4 — Judul",
    why_card_4_desc: "Kartu 4 — Deskripsi",
    workshop_overline: "Workshop — Overline",
    contact_whatsapp: "Kontak — Nomor WhatsApp (628xx)",
    contact_whatsapp_text: "Kontak — Template Pesan WhatsApp ({product} akan otomatis diganti nama produk)",
    workshop_title: "Workshop — Judul",
    workshop_quote: "Workshop — Quote founder",
    workshop_image_1: "Workshop — URL gambar 1",
    workshop_image_2: "Workshop — URL gambar 2",
    tier_overline: "Tier — Overline",
    tier_title: "Tier — Judul section",
    tier_subtitle: "Tier — Subjudul section",
    tier_reseller_title: "Box Reseller — Judul",
    tier_reseller_pricelabel: "Box Reseller — Label harga",
    tier_reseller_minorder: "Box Reseller — Minimum order (ex: 3 pcs)",
    tier_reseller_benefits: "Box Reseller — Benefit (pisahkan dengan | )",
    tier_reseller_cta: "Box Reseller — Teks tombol",
    tier_agen_title: "Box Agen — Judul",
    tier_agen_pricelabel: "Box Agen — Label harga",
    tier_agen_minorder: "Box Agen — Minimum order (ex: 12 pcs)",
    tier_agen_benefits: "Box Agen — Benefit (pisahkan dengan | )",
    tier_agen_cta: "Box Agen — Teks tombol",
    tier_distributor_title: "Box Distributor — Judul",
    tier_distributor_pricelabel: "Box Distributor — Label harga",
    tier_distributor_minorder: "Box Distributor — Minimum order (ex: 60 pcs)",
    tier_distributor_benefits: "Box Distributor — Benefit (pisahkan dengan | )",
    tier_distributor_cta: "Box Distributor — Teks tombol",
    form_overline: "Form — Overline",
    form_title: "Form — Judul",
    form_subtitle: "Form — Subjudul",
    promo_banner: "Banner Promo (kosongkan jika tidak ada)",
  },
  elearning: {
    hero_overline: "Hero — Overline",
    hero_title: "Hero — Judul",
    hero_subtitle: "Hero — Subjudul",
  },
  feedback: {
    hero_overline: "Hero — Overline",
    hero_title: "Hero — Judul",
    hero_subtitle: "Hero — Subjudul",
  },
  contact: {
    whatsapp: "WhatsApp — Angka (untuk link, ex: 087792682795)",
    whatsapp_display: "WhatsApp — Tampilan (ex: 0877-9268-2795)",
    instagram: "Instagram username (tanpa @)",
    tiktok: "TikTok username (tanpa @)",
    address: "Alamat Lengkap Workshop",
  },
};

const HOMEPAGE_SECTIONS = [
  { title: "Hero", prefix: "hero_" },
  { title: "Statistik", prefix: "stat_" },
  { title: "Koleksi Aurora", prefix: "catalog_" },
  { title: "Mengapa Aurora", prefix: "why_" },
  { title: "Workshop", prefix: "workshop_" },
  { title: "Program Kemitraan (Tier)", prefix: "tier_" },
  { title: "Form Pendaftaran", prefix: "form_" },
  { title: "Banner Promo", prefix: "promo_" },
  { title: "Kontak WhatsApp", prefix: "contact_" },
];

// Icon options aligned with lucide-react icons imported in HomePage.jsx
const ICON_OPTIONS = [
  "Scissors",
  "ShieldCheck",
  "Truck",
  "Star",
  "Package",
  "Sparkles",
  "Award",
  "Heart",
  "Gem",
  "Palette",
  "Users",
  "Zap",
  "Crown",
  "ThumbsUp",
  "Leaf",
  "Flame",
  "Check",
];

export default function CMSTab() {
  const [page, setPage] = useState("homepage");
  const [content, setContent] = useState({});
  const [changes, setChanges] = useState({});
  const [saving, setSaving] = useState(false);
  const [uploadingKey, setUploadingKey] = useState("");
  const fileRefs = useRef({});

  const load = async () => {
    try {
      const r = await api.get(`/content/${page}`);
      setContent(r.data);
      setChanges({});
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  const setValue = (key, value) => {
    setChanges((prev) => ({ ...prev, [key]: value }));
  };

  const save = async () => {
    if (Object.keys(changes).length === 0) {
      toast.info("Tidak ada perubahan");
      return;
    }
    setSaving(true);
    try {
      await api.put(`/admin/content/${page}`, changes);
      toast.success("Perubahan disimpan");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const upload = async (key, file) => {
    if (!file) return;
    setUploadingKey(key);
    try {
      const url = await uploadToCloudinary(file, "image", "aurora/banners");
      setValue(key, url);
      toast.success("Gambar terupload");
    } catch (e) {
      toast.error("Upload gagal: " + e.message);
    } finally {
      setUploadingKey("");
    }
  };

  const fields = FIELD_LABELS[page] || {};
  // Also include keys present in content but not in explicit labels
  Object.keys(content).forEach((k) => {
    if (!(k in fields)) fields[k] = k;
  });

  const renderField = (key, label) => {
    const currentValue = key in changes ? changes[key] : (content[key] || "");
    const isIcon = key.toLowerCase().endsWith("_icon");
    const isImage =
      !isIcon &&
      (key.toLowerCase().includes("image") ||
        key.toLowerCase().includes("_url") ||
        key.toLowerCase().includes("banner"));
    const isLong = !isImage && !isIcon && typeof currentValue === "string" && currentValue.length > 80;
    return (
      <div key={key}>
        <label className="block overline text-stone-500 mb-2">{label}</label>
        <div className="flex gap-2 items-start">
          {isIcon ? (
            <select
              data-testid={`cms-${key}`}
              value={currentValue || ""}
              onChange={(e) => setValue(key, e.target.value)}
              className="flex-1 h-11 px-3 border border-stone-300 focus:border-[#F39237] outline-none bg-white"
            >
              <option value="">— Pilih ikon —</option>
              {ICON_OPTIONS.map((opt) => (
                <option key={opt} value={opt}>
                  {opt}
                </option>
              ))}
            </select>
          ) : isLong ? (
            <textarea
              data-testid={`cms-${key}`}
              rows={3}
              value={currentValue}
              onChange={(e) => setValue(key, e.target.value)}
              className="flex-1 px-3 py-2 border border-stone-300 focus:border-[#F39237] outline-none"
            />
          ) : (
            <input
              data-testid={`cms-${key}`}
              type="text"
              value={currentValue}
              onChange={(e) => setValue(key, e.target.value)}
              className="flex-1 h-11 px-3 border border-stone-300 focus:border-[#F39237] outline-none"
            />
          )}
          {isImage && (
            <>
              <button
                type="button"
                onClick={() => fileRefs.current[key]?.click()}
                disabled={uploadingKey === key}
                data-testid={`cms-upload-${key}`}
                className="h-11 px-3 border border-stone-300 hover:border-[#F39237] hover:text-[#F39237] transition-colors flex items-center gap-2 text-sm flex-shrink-0"
              >
                <Upload size={14} />
                {uploadingKey === key ? "…" : "Upload"}
              </button>
              <input
                ref={(el) => {
                  fileRefs.current[key] = el;
                }}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => upload(key, e.target.files?.[0])}
              />
            </>
          )}
        </div>
        {isImage && currentValue && (
          <img src={currentValue} alt="" className="mt-2 h-20 w-auto object-cover border border-stone-200" />
        )}
      </div>
    );
  };

  return (
    <div className="space-y-5" data-testid="cms-tab">
      <div className="flex gap-2 border-b border-stone-200 flex-wrap">
        {PAGES.map((p) => (
          <button
            key={p.key}
            onClick={() => setPage(p.key)}
            data-testid={`cms-page-${p.key}`}
            className={`px-5 py-3 text-sm border-b-2 transition-colors ${
              page === p.key ? "border-[#F39237] text-[#0A0A0A] font-medium" : "border-transparent text-stone-500 hover:text-[#0A0A0A]"
            }`}
          >
            {p.label}
          </button>
        ))}
      </div>

      <div className="bg-white border border-stone-200 p-5 md:p-8 space-y-5">
        {page === "homepage" ? (
          HOMEPAGE_SECTIONS.map((sec) => {
            const sectionEntries = Object.entries(fields).filter(([k]) => k.startsWith(sec.prefix));
            if (sectionEntries.length === 0) return null;
            return (
              <div key={sec.title} data-testid={`cms-section-${sec.prefix.replace(/_$/, "")}`}>
                <div className="border-l-4 border-[#F39237] pl-4 mb-5">
                  <div className="overline text-[#F39237]">Section</div>
                  <h4 className="font-display text-xl text-[#0A0A0A]">{sec.title}</h4>
                </div>
                <div className="space-y-5 pb-2">
                  {sectionEntries.map(([key, label]) => renderField(key, label))}
                </div>
                <div className="border-b border-stone-100 mt-6" />
              </div>
            );
          })
        ) : (
          Object.entries(fields).map(([key, label]) => renderField(key, label))
        )}

        <div className="pt-4 border-t border-stone-200 sticky bottom-0 bg-white -mx-5 md:-mx-8 px-5 md:px-8 pb-2">
          <button
            onClick={save}
            disabled={saving || Object.keys(changes).length === 0}
            data-testid="cms-save"
            className="inline-flex items-center gap-2 px-6 py-3 bg-[#0A0A0A] text-white hover:bg-[#F39237] hover:text-[#0A0A0A] disabled:opacity-60"
          >
            <Save size={16} />
            {saving ? "Menyimpan…" : `Simpan Perubahan (${Object.keys(changes).length})`}
          </button>
        </div>
      </div>
    </div>
  );
}
